import axios from "axios";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { SecureAuth } from "asset/icons";
import ErrorMessage from "../ErrorMessage";
import Loader from "component/loader";
import { COMPLETE_LOGIN } from "store/action";
import { checkAutoLogin } from "store/authServices";

const BackupVerification = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [backupCode, setBackupCode] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const pendingLogin = sessionStorage.getItem("pendingLogin");

        if (!pendingLogin) {
            navigate("/auth/signin", { replace: true });
        }
    }, [navigate]);

    const handleVerify = async () => {
        if (!backupCode.trim()) {
            setError("Please enter your backup verification code.");
            return;
        }

        const pendingLoginString = sessionStorage.getItem("pendingLogin");
        if (!pendingLoginString) {
            setError("Your sign-in session expired. Please sign in again.");
            return navigate("/auth/signin", { replace: true });
        }

        const pendingLogin = JSON.parse(pendingLoginString);

        try {
            setLoading(true);
            setError("");

            const verifyResponse = await axios.post(
                "/v1/users/mfa-backup-code",
                { code: backupCode.trim() },
                {
                    headers: {
                        Authorization: `Bearer ${pendingLogin.token}`,
                    },
                }
            );

            const verifiedLoginData = verifyResponse?.data?.data || {};

            if (!verifiedLoginData?.token) {
                throw new Error("Missing permanent token from backup verification response.");
            }

            COMPLETE_LOGIN(verifiedLoginData, dispatch);
            sessionStorage.removeItem("pendingLogin");
            await checkAutoLogin(dispatch);

            if (verifiedLoginData?.user?.pinStatus === "NOT_VERIFIED") {
                return navigate("/auth/setup-pin", { replace: true });
            }

            const lastVisitedPath = sessionStorage.getItem("lastVisitedPath");
            if (
                lastVisitedPath &&
                lastVisitedPath !== "/" &&
                lastVisitedPath !== "/auth/signin"
            ) {
                sessionStorage.removeItem("lastVisitedPath");
                return navigate(lastVisitedPath, { replace: true });
            }

            navigate("/dashboard", { replace: true });
        } catch (verifyError) {
            setError(
                verifyError?.response?.data?.message ||
                    verifyError?.message ||
                    "Invalid backup verification code. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white rounded-xl w-full max-w-[650px] mx-auto border border-[#EBEDF9]">
            <div className="!pt-5 pl-2">
                <SecureAuth />
            </div>

            <div className="px-8 pb-8">
                <h2 className="text-2xl font-bold text-[#101828]">
                    Enter backup verification code
                </h2>
                <p className="mt-2 text-sm leading-7 text-[#667085]">
                    Provide your 12-digit backup verification code given to you when you set up your
                    2FA.
                </p>

                <div className="mt-8">
                    <input
                        type="text"
                        value={backupCode}
                        onChange={(event) => {
                            setBackupCode(event.target.value);
                            setError("");
                        }}
                        placeholder="Backup verification code"
                        className="h-16 w-full rounded-xl bg-[#F2F4F7] px-6 text-[#344054] outline-none transition placeholder:text-[#667085] focus:border-[#6F00FF] focus:ring-2 focus:ring-[#E9D7FE]"
                    />
                </div>

                <div className="mt-4">
                    <ErrorMessage message={error} />
                </div>

                <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <button
                        type="button"
                        className="text-left text-sm font-medium text-[#6F00FF] hover:underline"
                    >
                        Need help? Contact support
                    </button>

                    <div className="flex items-center gap-6">
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="text-sm font-medium text-[#101828] hover:underline"
                        >
                            Go back
                        </button>

                        <button
                            type="button"
                            onClick={handleVerify}
                            disabled={!backupCode.trim() || loading}
                            className={`rounded-lg bg-[#6F00FF] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#6f00fff0] ${
                                !backupCode.trim() || loading
                                    ? "cursor-not-allowed opacity-50"
                                    : ""
                            }`}
                        >
                            {loading ? <Loader /> : "Verify"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BackupVerification;
